// AdminUsers Component - User management
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Users, Edit, Loader2, DollarSign, UserCog, Save, KeyRound, Lock, LockOpen } from 'lucide-react';
import { formatCAD, formatTransactionAmount } from '../../utils/currency';
import { useAdmin } from './AdminContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers({ users: propUsers, adminKey, onRefresh }) {
  // Always call useAdmin hook (required by React rules)
  const contextValues = useAdmin() || {};
  
  // Use props if provided, otherwise fallback to context
  const users = propUsers || contextValues.users || [];
  const api = contextValues.api || axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey },
    withCredentials: true
  });
  const fetchUsers = onRefresh || contextValues.fetchUsers || (() => {});
  const formatDate = contextValues.formatDate || ((d) => d ? new Date(d).toLocaleDateString('fr-CA') : 'N/A');
  const language = contextValues.language || 'fr';
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editingUserData, setEditingUserData] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [userTransactions, setUserTransactions] = useState([]);
  const [userWallet, setUserWallet] = useState(null);
  const [resetPwDialog, setResetPwDialog] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [resettingPw, setResettingPw] = useState(false);
  const [loginLocked, setLoginLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const handleUnlockLogin = async () => {
    if (!selectedUser?.email) return;
    setUnlocking(true);
    try {
      await api.post(`/admin/users/unlock-login?email=${encodeURIComponent(selectedUser.email)}`);
      toast.success(language === 'fr' ? 'Connexion déverrouillée' : 'Login unlocked');
      setLoginLocked(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setUnlocking(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (!newUserPassword || newUserPassword.length < 6) {
      toast.error(language === 'fr'
        ? 'Le mot de passe doit contenir au moins 6 caractères'
        : 'Password must be at least 6 characters');
      return;
    }
    setResettingPw(true);
    try {
      await api.put(`/admin/users/${selectedUser.user_id}/reset-password`, { new_password: newUserPassword });
      toast.success(language === 'fr' ? 'Mot de passe réinitialisé' : 'Password reset');
      setResetPwDialog(false);
      setNewUserPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setResettingPw(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      setSelectedUser(response.data.user);
      setUserWallet(response.data.wallet);
      setUserTransactions(response.data.transactions || []);
      setLoginLocked(!!response.data.login_locked);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Error loading user');
    }
  };

  const handleEditBalance = async () => {
    if (!selectedUser || !newBalance) return;
    try {
      await api.put(`/admin/users/${selectedUser.user_id}/balance?amount=${parseFloat(newBalance)}`);
      toast.success(language === 'fr' ? 'Solde mis à jour' : 'Balance updated');
      setBalanceDialog(false);
      setNewBalance('');
      fetchUserDetails(selectedUser.user_id);
      fetchUsers();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Error updating balance');
    }
  };

  const openEditUserDialog = (user) => {
    setEditingUserData({
      user_id: user.user_id,
      name: user.name || '',
      email: user.email || '',
      language: user.language || 'fr',
      account_type: user.account_type || 'citoyen',
      is_active: user.is_active !== false,
      notes: user.admin_notes || ''
    });
    setEditUserDialog(true);
  };

  const saveUserChanges = async () => {
    if (!editingUserData) return;
    setSavingUser(true);
    try {
      await api.put(`/admin/users/${editingUserData.user_id}`, {
        name: editingUserData.name,
        email: editingUserData.email,
        language: editingUserData.language,
        account_type: editingUserData.account_type,
        is_active: editingUserData.is_active,
        notes: editingUserData.notes
      });
      toast.success(language === 'fr' ? 'Utilisateur mis à jour' : 'User updated');
      setEditUserDialog(false);
      fetchUsers();
      if (selectedUser?.user_id === editingUserData.user_id) {
        fetchUserDetails(editingUserData.user_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSavingUser(false);
    }
  };

  const impersonateUser = async (userId) => {
    setImpersonating(true);
    try {
      const response = await api.post(`/admin/users/${userId}/impersonate`);
      // Impersonation session is set as an httpOnly cookie by the backend.
      localStorage.setItem('payhub_impersonating', 'true');
      localStorage.setItem('payhub_impersonated_user', JSON.stringify(response.data.user));
      toast.success(language === 'fr' ? 'Connexion en tant qu\'utilisateur...' : 'Logging in as user...');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur d\'impersonation' : 'Impersonation error');
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <Users className="h-5 w-5 text-white" />
            </div>
            {language === 'fr' ? 'Tous les Utilisateurs' : 'All Users'}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Gérer les comptes et les soldes' : 'Manage accounts and balances'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.map((user) => (
              <div 
                key={user.user_id} 
                data-testid={`admin-user-row-${user.user_id}`}
                className="flex items-center justify-between p-4 rounded-xl border border-border hover:bg-muted/50 cursor-pointer transition-colors" 
                onClick={() => fetchUserDetails(user.user_id)}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-10 w-10 rounded-full" />
                    ) : (
                      <Users className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {user.language?.toUpperCase()} • {formatDate(user.created_at)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={!!selectedUser && !balanceDialog && !editUserDialog && !resetPwDialog} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                {selectedUser?.picture ? (
                  <img src={selectedUser.picture} alt="" className="h-12 w-12 rounded-full" />
                ) : (
                  <Users className="h-6 w-6 text-primary" />
                )}
              </div>
              <div>
                <p>{selectedUser?.name}</p>
                <p className="text-sm font-normal text-muted-foreground">{selectedUser?.email}</p>
              </div>
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' ? 'Détails et actions du compte utilisateur' : 'User account details and actions'}
            </DialogDescription>
            {loginLocked && (
              <div data-testid="login-locked-badge" className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 w-fit">
                <Lock className="h-3.5 w-3.5" />
                {language === 'fr' ? 'Connexion verrouillée (trop de tentatives)' : 'Login locked (too many attempts)'}
              </div>
            )}
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              {/* Wallet Info */}
              {userWallet && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white">
                  <p className="text-sm opacity-90">{language === 'fr' ? 'Solde du portefeuille' : 'Wallet Balance'}</p>
                  <p className="text-3xl font-bold">{formatCAD(userWallet.balance)}</p>
                </div>
              )}
              
              {/* User Info Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">{language === 'fr' ? 'Langue:' : 'Language:'}</span> {selectedUser.language?.toUpperCase()}</div>
                <div><span className="text-muted-foreground">{language === 'fr' ? 'Type:' : 'Type:'}</span> {selectedUser.account_type || 'citoyen'}</div>
                <div><span className="text-muted-foreground">{language === 'fr' ? 'Créé:' : 'Created:'}</span> {formatDate(selectedUser.created_at)}</div>
                <div><span className="text-muted-foreground">{language === 'fr' ? 'Statut:' : 'Status:'}</span> {selectedUser.is_active !== false ? '✅ Actif' : '❌ Inactif'}</div>
              </div>

              {/* Recent Transactions */}
              {userTransactions.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">{language === 'fr' ? 'Transactions récentes' : 'Recent Transactions'}</h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {userTransactions.slice(0, 5).map((tx) => (
                      <div key={tx.transaction_id} className="flex justify-between text-sm p-2 rounded bg-muted/50">
                        <span>{tx.description}</span>
                        <span className={tx.amount > 0 ? 'text-green-600' : 'text-red-500'}>
                          {formatTransactionAmount(tx.amount, tx.amount > 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4">
                <Button variant="outline" onClick={() => openEditUserDialog(selectedUser)} className="flex-1 min-w-[120px]">
                  <Edit className="h-4 w-4 mr-2" />
                  {language === 'fr' ? 'Modifier' : 'Edit'}
                </Button>
                <Button variant="outline" onClick={() => { setNewBalance(userWallet?.balance?.toString() || '0'); setBalanceDialog(true); }} className="flex-1 min-w-[120px]">
                  <DollarSign className="h-4 w-4 mr-2" />
                  {language === 'fr' ? 'Modifier solde' : 'Edit Balance'}
                </Button>
                <Button variant="outline" onClick={() => { setNewUserPassword(''); setResetPwDialog(true); }} className="flex-1 min-w-[120px]" data-testid="open-reset-password-btn">
                  <KeyRound className="h-4 w-4 mr-2" />
                  {language === 'fr' ? 'Mot de passe' : 'Password'}
                </Button>
                {loginLocked && (
                  <Button variant="outline" onClick={handleUnlockLogin} disabled={unlocking} className="flex-1 min-w-[120px] border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300" data-testid="unlock-login-btn">
                    {unlocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LockOpen className="h-4 w-4 mr-2" />}
                    {language === 'fr' ? 'Déverrouiller' : 'Unlock'}
                  </Button>
                )}
                <Button onClick={() => impersonateUser(selectedUser.user_id)} disabled={impersonating} className="flex-1 min-w-[120px]" data-testid="impersonate-btn">
                  {impersonating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCog className="h-4 w-4 mr-2" />}
                  {language === 'fr' ? 'Se connecter' : 'Login as'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Balance Dialog */}
      <Dialog open={balanceDialog} onOpenChange={setBalanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Modifier le solde' : 'Edit Balance'}</DialogTitle>
            <DialogDescription>
              {language === 'fr' ? 'Définir un nouveau solde de portefeuille (CAD)' : 'Set a new wallet balance (CAD)'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{language === 'fr' ? 'Nouveau solde (CAD)' : 'New Balance (CAD)'}</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={newBalance} 
                onChange={(e) => setNewBalance(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBalanceDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleEditBalance}>
              <Save className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwDialog} onOpenChange={(open) => { setResetPwDialog(open); if (!open) setNewUserPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              {language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset Password'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <DialogDescription>
              {language === 'fr'
                ? `Définir un nouveau mot de passe pour ${selectedUser?.name} (${selectedUser?.email}).`
                : `Set a new password for ${selectedUser?.name} (${selectedUser?.email}).`}
            </DialogDescription>
            <div>
              <Label>{language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}</Label>
              <Input
                type="text"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder={language === 'fr' ? 'Au moins 6 caractères' : 'At least 6 characters'}
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleResetPassword} disabled={resettingPw} data-testid="confirm-reset-password-btn">
              {resettingPw ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {language === 'fr' ? 'Réinitialiser' : 'Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Modifier l\'utilisateur' : 'Edit User'}</DialogTitle>
            <DialogDescription>
              {language === 'fr' ? 'Mettre à jour les informations du compte' : 'Update account information'}
            </DialogDescription>
          </DialogHeader>
          {editingUserData && (
            <div className="space-y-4">
              <div>
                <Label>{language === 'fr' ? 'Nom' : 'Name'}</Label>
                <Input 
                  value={editingUserData.name} 
                  onChange={(e) => setEditingUserData({...editingUserData, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editingUserData.email} 
                  onChange={(e) => setEditingUserData({...editingUserData, email: e.target.value})}
                />
              </div>
              <div>
                <Label>{language === 'fr' ? 'Langue' : 'Language'}</Label>
                <Select value={editingUserData.language} onValueChange={(v) => setEditingUserData({...editingUserData, language: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{language === 'fr' ? 'Type de compte' : 'Account Type'}</Label>
                <Select value={editingUserData.account_type} onValueChange={(v) => setEditingUserData({...editingUserData, account_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="citoyen">Citoyen</SelectItem>
                    <SelectItem value="marchand">Marchand</SelectItem>
                    <SelectItem value="organisme">Organisme</SelectItem>
                    <SelectItem value="partenaire">Partenaire</SelectItem>
                    <SelectItem value="ambassadeur">Ambassadeur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{language === 'fr' ? 'Notes admin' : 'Admin Notes'}</Label>
                <Textarea 
                  value={editingUserData.notes} 
                  onChange={(e) => setEditingUserData({...editingUserData, notes: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={saveUserChanges} disabled={savingUser}>
              {savingUser ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
